/**
 * monetization/config — PURE configuration resolution.
 *
 * Three layers, from lowest to highest precedence (identical to Rust
 * `core-rust/src/monetization/resolve.rs`):
 *   1. Code defaults (free ad-supported + pro ad-free).
 *   2. Environment variables (`MONETIZATION_ENABLED`, `PAYMENTS_PROVIDER`,
 *      `TIER_FREE_STORAGE_MB`, `TIER_PRO_MAX_ITEMS`, `TIER_FREE_ADS`, …).
 *      `-1` = unlimited.
 *   3. Persisted overrides edited by the admin UI (`monetization_config`).
 *
 * Env and UI thus share the SAME configuration source: the env provides the
 * defaults and each override row overwrites ONE key on the fly; deleting the
 * override returns control to the env.
 */

import {
  type MonetizationConfig,
  type MonetizationOverrideRow,
  QUOTA_KEYS,
  type QuotaKey,
  type QuotaLimits,
  type TierDef,
} from "./types";

const MB = 1024 * 1024;

/** Type guard: is `field` a valid `QuotaKey`? Avoids an unsafe `as QuotaKey`.*/
function isQuotaKey(field: string): field is QuotaKey {
  return (QUOTA_KEYS as readonly string[]).includes(field);
}

/** Env suffixes supported by quota: `TIER_<ID>_<SUFFIX>`.*/
const ENV_QUOTA_SUFFIXES: readonly (readonly [string, QuotaKey])[] = [
  ["STORAGE_MB", "storage_bytes"],
  ["MAX_ITEMS", "items"],
  ["MAX_COLLECTIONS", "collections"],
  ["MAX_ORGS", "orgs"],
  ["MAX_ATTACHMENTS", "attachments"],
];

export function emptyLimits(): QuotaLimits {
  return {
    storage_bytes: null,
    items: null,
    collections: null,
    orgs: null,
    attachments: null,
  };
}

/**
 * Code defaults embodying the portfolio's business model:
 * free = ad-supported (entry-level revenue) with generous-but-limited quotas;
 * pro = ad-free + more storage. Free also covers guests.
 */
export function builtinDefaults(): MonetizationConfig {
  return {
    enabled: false,
    paymentsEnabled: false,
    provider: "none",
    defaultTier: "free",
    tiers: [
      {
        id: "free",
        label: "Free",
        adsEnabled: true,
        limits: {
          storage_bytes: 50 * MB,
          items: 500,
          collections: 20,
          orgs: 1,
          attachments: 100,
        },
      },
      {
        id: "pro",
        label: "Pro",
        adsEnabled: false,
        limits: {
          storage_bytes: 5 * 1024 * MB,
          items: null,
          collections: null,
          orgs: 10,
          attachments: null,
        },
      },
    ],
  };
}

function parseBool(v: string | undefined): boolean | undefined {
  if (v === undefined) {
    return undefined;
  }
  const s = v.trim().toLowerCase();
  if (["1", "true", "yes", "on"].includes(s)) {
    return true;
  }
  if (["0", "false", "no", "off"].includes(s)) {
    return false;
  }
  return undefined;
}

/** `-1` (or negative) ⇒ unlimited (null). Non-numeric ⇒ undefined (ignored).*/
function parseLimit(v: string): number | null | undefined {
  const n = Number.parseInt(v.trim(), 10);
  if (Number.isNaN(n)) {
    return undefined;
  }
  return n < 0 ? null : n;
}

function capitalize(s: string): string {
  return s.length > 0 ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function tierByIdOrCreate(config: MonetizationConfig, id: string): TierDef {
  const existing = config.tiers.find((t) => t.id === id);
  if (existing) {
    return existing;
  }
  const created: TierDef = {
    id,
    label: capitalize(id),
    // New tier discovered by config: assumed to be paid (ad-free) unless
    // TIER_<ID>_ADS / tier.<id>.ads_enabled say otherwise.
    adsEnabled: false,
    limits: emptyLimits(),
  };
  config.tiers.push(created);
  return created;
}

/** Layer 2: applies environment variables over the defaults.*/
export function applyEnv(
  config: MonetizationConfig,
  env: Record<string, string | undefined>,
): void {
  const enabled = parseBool(env.MONETIZATION_ENABLED);
  if (enabled !== undefined) {
    config.enabled = enabled;
  }
  const payments = parseBool(env.PAYMENTS_ENABLED);
  if (payments !== undefined) {
    config.paymentsEnabled = payments;
  }
  const provider = env.PAYMENTS_PROVIDER?.trim();
  if (provider) {
    config.provider = provider.toLowerCase();
  }
  const defaultTier = env.MONETIZATION_DEFAULT_TIER?.trim();
  if (defaultTier) {
    config.defaultTier = defaultTier.toLowerCase();
  }

  // TIER_<ID>_<SUFFIX>: discovers tiers dynamically (TIER_TEAM_MAX_ITEMS creates "team").
  for (const [key, raw] of Object.entries(env)) {
    if (!key.startsWith("TIER_") || raw === undefined) {
      continue;
    }
    const rest = key.slice("TIER_".length);
    for (const [suffix, quotaKey] of ENV_QUOTA_SUFFIXES) {
      if (!rest.endsWith(`_${suffix}`)) {
        continue;
      }
      const tierId = rest.slice(0, rest.length - suffix.length - 1);
      if (!tierId) {
        continue;
      }
      const limit = parseLimit(raw);
      if (limit === undefined) {
        continue;
      }
      const value = quotaKey === "storage_bytes" && limit !== null ? limit * MB : limit;
      tierByIdOrCreate(config, tierId.toLowerCase()).limits[quotaKey] = value;
    }
    if (rest.endsWith("_LABEL")) {
      const tierId = rest.slice(0, rest.length - "_LABEL".length);
      if (tierId && raw.trim()) {
        tierByIdOrCreate(config, tierId.toLowerCase()).label = raw.trim();
      }
    }
    // TIER_<ID>_ADS: is the tier ad-supported? (profitable-free model).
    if (rest.endsWith("_ADS")) {
      const tierId = rest.slice(0, rest.length - "_ADS".length);
      const ads = parseBool(raw);
      if (tierId && ads !== undefined) {
        tierByIdOrCreate(config, tierId.toLowerCase()).adsEnabled = ads;
      }
    }
  }
}

function valueAsLimit(value: unknown): number | null | undefined {
  if (typeof value !== "number" || !Number.isInteger(value)) {
    return undefined;
  }
  return value < 0 ? null : value;
}

/**
 * Layer 3: applies a back-office override. Logical keys:
 * `monetization.enabled`, `payments.enabled`, `payments.provider`,
 * `monetization.default_tier`, `tier.<id>.label`, `tier.<id>.ads_enabled`,
 * `tier.<id>.<quota_key>` (-1 = unlimited; storage is ALWAYS in bytes).
 */
export function applyOverride(config: MonetizationConfig, key: string, value: unknown): void {
  switch (key) {
    case "monetization.enabled": {
      if (typeof value === "boolean") {
        config.enabled = value;
      }
      return;
    }
    case "payments.enabled": {
      if (typeof value === "boolean") {
        config.paymentsEnabled = value;
      }
      return;
    }
    case "payments.provider": {
      if (typeof value === "string" && value.trim()) {
        config.provider = value.trim().toLowerCase();
      }
      return;
    }
    case "monetization.default_tier": {
      if (typeof value === "string" && value.trim()) {
        config.defaultTier = value.trim().toLowerCase();
      }
      return;
    }
    default: {
      if (!key.startsWith("tier.")) {
        return;
      }
      const rest = key.slice("tier.".length);
      const dot = rest.indexOf(".");
      if (dot <= 0) {
        return;
      }
      const tierId = rest.slice(0, dot).toLowerCase();
      const field = rest.slice(dot + 1);
      const tier = tierByIdOrCreate(config, tierId);
      if (field === "label") {
        if (typeof value === "string" && value.trim()) {
          tier.label = value.trim();
        }
      } else if (field === "ads_enabled") {
        if (typeof value === "boolean") {
          tier.adsEnabled = value;
        }
      } else if (isQuotaKey(field)) {
        const limit = valueAsLimit(value);
        if (limit !== undefined) {
          tier.limits[field] = limit;
        }
      }
    }
  }
}

/** Is this a valid override key? (same validation as the admin endpoint).*/
export function isValidOverrideKey(key: string): boolean {
  if (
    [
      "monetization.enabled",
      "payments.enabled",
      "payments.provider",
      "monetization.default_tier",
    ].includes(key)
  ) {
    return true;
  }
  if (!key.startsWith("tier.")) {
    return false;
  }
  const rest = key.slice("tier.".length);
  const dot = rest.indexOf(".");
  if (dot <= 0) {
    return false;
  }
  const field = rest.slice(dot + 1);
  return field === "label" || field === "ads_enabled" || isQuotaKey(field);
}

/** Full resolution: defaults → env → overrides (in that order).*/
export function resolveMonetizationConfig(
  env: Record<string, string | undefined>,
  overrides: readonly MonetizationOverrideRow[] = [],
): MonetizationConfig {
  const config = builtinDefaults();
  applyEnv(config, env);
  for (const o of overrides) {
    applyOverride(config, o.key, o.value);
  }
  return config;
}

/** Effective tier (falls back to defaultTier if the id does not exist).*/
export function tierFor(
  config: MonetizationConfig,
  tierId: string | undefined,
): TierDef | undefined {
  return (
    config.tiers.find((t) => t.id === tierId) ??
    config.tiers.find((t) => t.id === config.defaultTier)
  );
}

/**
 * Contract with the AdProvider (growth canon): should the app show ads to
 * this user? Only with monetization ON and an ad-supported tier. Guests
 * (without a tier) inherit the default (free ⇒ ads on: free is entry-level profitable).
 */
export function shouldShowAds(config: MonetizationConfig, tierId?: string): boolean {
  if (!config.enabled) {
    return false;
  }
  return tierFor(config, tierId)?.adsEnabled ?? false;
}
